import React, { useState, useEffect, ReactElement, useMemo } from 'react';
import { useRouter } from 'next/router';

import { getLocationFromIP } from '../modules/ip';
import { LayoutSkeleton } from '../containers/layout';
import config from '../../config';
import { stringifyURL } from '../modules/formatting';
import { useBackfilledSuppliers } from '../hooks/useBackfilledSuppliers';
import { EventsPageProps } from '../types';
import { captureError } from '../modules/monitoring';
import { compressData } from '@/helpers/compressData';
import { decompressData } from '@/helpers/decompressData';
import VerticalPageV2 from '../containers/vertical/v2';
import { getSuppliersForVerticalV2 } from '../modules/suppliersV2';
import { MAX_OFFLINE_SUPPLIERS_V2 } from '../constants/suppliers';
import getEventsPageV2 from '../modules/getEventsPage/v2';
import { isArrayNotEmpty } from '@/helpers/array';
import { ErrorPage } from '../components/ErrorPage';
import {
  ERROR_CONTENT_TYPES,
  ERROR_CONTENT,
} from '../constants/error-page-content';
import { shouldBlock } from '@/helpers/blockedPages';

export interface LocationParams {
  lat: string;
  lon: string;
  sort: string;
}

function EventsPage({
  compressedProps,
  isBlocked,
}: {
  compressedProps?: string;
  isBlocked?: boolean;
}): ReactElement {
  const { isFallback } = useRouter();

  // Decompress props synchronously to avoid blocking render
  const decompressedProps = useMemo(() => {
    if (isBlocked) return null;
    try {
      return decompressData<EventsPageProps>(compressedProps);
    } catch (error) {
      captureError('Error decompressing props:', error);
      return null;
    }
  }, [compressedProps, isBlocked]);

  // State management for "near me" suppliers
  const [suppliersNearMe, setSuppliersNearMe] = useState(null);
  const [nearMeCity, setNearMeCity] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const verticalName =
    decompressedProps?.pageProperties?.pageContent?.seoVertical;

  // Backfill with offline supplier from Airtable if online suppliers are less than the maximum allowed
  const [
    backfilledSuppliers,
    isLoadingBackfilledSuppliers,
  ] = useBackfilledSuppliers(
    suppliersNearMe || decompressedProps?.suppliers,
    MAX_OFFLINE_SUPPLIERS_V2,
    decompressedProps?.pageProperties?.locationVariable,
    verticalName
  );

  // Fetch "near me" suppliers
  useEffect(() => {
    if (isBlocked || !decompressedProps?.pageProperties?.isNearMe) return;

    const fetchSuppliersNearMe = async () => {
      setIsLoading(true);
      try {
        const { latitude, longitude, city } = await getLocationFromIP();

        const locationParams: LocationParams = {
          lat:
            decompressedProps.pageProperties.locationVariable?.lat || latitude,
          lon:
            decompressedProps.pageProperties.locationVariable?.lon || longitude,
          sort: 'distance',
        };

        const nearMeSuppliers = await getSuppliersForVerticalV2({
          supplierData: decompressedProps.supplierData,
          locationParams,
          limit: MAX_OFFLINE_SUPPLIERS_V2,
          verticalName:
            decompressedProps.pageProperties?.pageContent?.seoVertical,
        });

        setSuppliersNearMe(nearMeSuppliers);
        setNearMeCity(
          decompressedProps.pageProperties.locationVariable?.inLocation ||
            decompressedProps.pageProperties.locationVariable?.city ||
            city ||
            ''
        );
      } catch (error) {
        captureError('Error fetching near me suppliers:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSuppliersNearMe();
  }, [decompressedProps, isBlocked]);

  // Determine which suppliers to display based on page type and available data
  const suppliersToDisplay = useMemo(() => {
    if (isBlocked) {
      return {
        results: [],
        count: 0,
        outOfRange: false,
        groups: [],
      };
    }

    let selectedSuppliers;

    // For near-me pages
    if (decompressedProps?.pageProperties?.isNearMe) {
      // Try near-me results first
      if (isArrayNotEmpty(suppliersNearMe?.results)) {
        selectedSuppliers = suppliersNearMe;
      }
      // Then try backfilled results
      else if (isArrayNotEmpty(backfilledSuppliers?.results)) {
        selectedSuppliers = backfilledSuppliers;
      }
      // Finally fall back to static suppliers
      else if (isArrayNotEmpty(decompressedProps?.suppliers?.results)) {
        selectedSuppliers = decompressedProps.suppliers;
      }
    } else {
      // For non-near-me pages
      // Try backfilled results first
      if (isArrayNotEmpty(backfilledSuppliers?.results)) {
        selectedSuppliers = backfilledSuppliers;
      }
      // Then try static props suppliers
      else if (isArrayNotEmpty(decompressedProps?.suppliers?.results)) {
        selectedSuppliers = decompressedProps.suppliers;
      }
    }

    // Default empty suppliers object if no valid suppliers found
    if (!selectedSuppliers) {
      selectedSuppliers = {
        results: [],
        count: 0,
        outOfRange: false,
        groups: [],
      };
    }

    return selectedSuppliers;
  }, [backfilledSuppliers, suppliersNearMe, decompressedProps, isBlocked]);

  // If this is a blocked page, render the 404 page content with proper status
  if (isBlocked) {
    return (
      <ErrorPage
        {...ERROR_CONTENT[ERROR_CONTENT_TYPES.NOT_FOUND_DEFAULT]}
        statusCode={410}
      />
    );
  }

  // Fallback behavior and loading state
  const isDataLoading = !decompressedProps || isFallback;

  if (isDataLoading) {
    return <LayoutSkeleton />;
  }

  // Render the main page
  return (
    <VerticalPageV2
      {...decompressedProps}
      suppliers={suppliersToDisplay}
      nearMeCity={nearMeCity}
      isLoading={
        !suppliersToDisplay?.results?.length &&
        (isLoading || isLoadingBackfilledSuppliers)
      }
    />
  );
}

export async function getServerSideProps({ params, res }) {
  // Build the path from params to check against blocked pages
  const pathSegments = params.vertical || [];
  const requestPath = `/${pathSegments.join('/')}`;

  // Check if this path should return 410
  // Skip blocking for static files and special paths
  if (
    requestPath.includes('.') || // Files with extensions
    requestPath.startsWith('/_next/') ||
    requestPath.startsWith('/api/') ||
    requestPath === '/sw.js' ||
    requestPath === '/robots.txt' ||
    requestPath === '/sitemap.xml'
  ) {
    // Continue with normal processing for static files
  } else if (shouldBlock(requestPath)) {
    // Set 410 status code
    res.statusCode = 410;
    res.setHeader('Cache-Control', 'public, max-age=86400, must-revalidate');

    return {
      props: {
        isBlocked: true,
      },
    };
  }

  const url = stringifyURL(`${config.baseUri}/api/events`, {
    vertical: params.vertical[0],
    variables: params.vertical.splice(1),
  });

  try {
    const props = await getEventsPageV2(url);

    if ('redirectPageId' in props) {
      return {
        redirect: {
          destination: `/${props.redirectPageId}`,
          permanent: false,
        },
      };
    }

    // Compress props
    const compressedProps = compressData(props);

    return {
      props: {
        compressedProps,
      },
    };
  } catch (error) {
    captureError('Error in getEventsPage:', error.message);

    // Return 404 for actual errors
    res.statusCode = 404;
    return {
      props: {
        isBlocked: true,
      },
    };
  }
}

export default EventsPage;
